import { ID } from '@datorama/akita';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, switchMap, take, tap, map, delay } from 'rxjs/operators';
import { AccountStatus, AccountTag, Acct, createTag } from './acct.model';
import { AcctsStore } from './accts.store';
import { SessionQuery } from '../../../core/state/session/session.query';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { Observable, combineLatest, from, of } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { ImpersonateQuery } from 'src/app/pages/admin/impersonate/state/impersonate.query';
import { Cacheable } from 'ts-cacheable';
import { ta } from 'date-fns/locale';
import { NewTagsService } from '../../../features/new-tags/state/tags.service';
import { NewTag } from '../../../features/new-tags/state/tags.model';

@Injectable({ providedIn: 'root' })
export class AcctsService {
    constructor(
        private store: AcctsStore,
        private http: HttpClient,
        private config: AppConfigQuery,
        private sessionQuery: SessionQuery,
        private keycloak: KeycloakService,
        private impersonateQuery: ImpersonateQuery,
        private _newTagsService: NewTagsService
    ) {
        const { keycloakRealm, useLatestKeycloakFlow } = this.config.get();
        const isAdmin$ = this.sessionQuery.isAdminUser$;

        const loggedIn$ = useLatestKeycloakFlow
            ? from(this.keycloak.isLoggedIn())
            : this.sessionQuery.isAuthenticated$;
        combineLatest([
            isAdmin$,
            loggedIn$,
            this.impersonateQuery.selectImpersonatedUser$
        ])
            .pipe(
                filter(([_, isAuthenticated, impersonatedUser]) => isAuthenticated),
                take(1),
                switchMap(([isAdmin, _, impersonatedUser]) =>
                    this.get(isAdmin && !impersonatedUser)
                )
            )
            .subscribe();
    }

    @Cacheable({ maxAge: 2000 })
    get(includeInactive = false) {
        return this.http
            .get<Acct[]>(`${this.config.get().adminBaseUrl}/v1/accounts`, {
                params: { includeInactive: includeInactive }
            })
            .pipe(
                tap((entities) => {
                    this.store.set(entities);
                })
            );
    }

    /** Get single account by id.
     * @param id - account id
     * @returns - observable for request
     */
    getById(id: ID) {
        return this.http
            .get<Acct>(`${this.config.get().adminBaseUrl}/v1/accounts/${id}`)
            .pipe(
                tap((acct) => {
                    this.store.upsert([acct.accountId], acct);
                })
            );
    }

    // TODO: refactor this to avoid creating an internal observable/subscriber
    /** Create account - create a new account.
     * @param data - name for the new account
     * @returns - observable for request
     */
    create(data: Partial<Acct>) {
        return this.http
            .post<Acct>(`${this.config.get().adminBaseUrl}/v1/accounts`, data)
            .pipe(
                tap((acct) => {
                    // TODO: this endpoint is not working, use it when fixed
                    // this.getById(acct.accountId).subscribe((a) =>
                    //     this.store.add(acct, {
                    //         prepend: true
                    //     })
                    // );
                    combineLatest([
                        this.sessionQuery.isAdminUser$,
                        this.sessionQuery.isAuthenticated$,
                        this.impersonateQuery.selectImpersonatedUser$
                    ])
                        .pipe(
                            filter(
                                ([isAdmin, isAuthenticated, impersonatedUser]) =>
                                    isAuthenticated
                            ),
                            take(1),
                            switchMap(([isAdmin, isAuthenticated, impersonatedUser]) =>
                                this.get(isAdmin && !impersonatedUser)
                            )
                        )
                        .subscribe();
                })
            );
    }

    /** Update - change account details
     * @param data -
     * @param id -
     * @returns - observable for request
     */
    update(data: Acct, id: ID) {
        return this.http
            .put<Acct>(`${this.config.get().adminBaseUrl}/v1/accounts/${id}`, data)
            .pipe(tap((account) => this.store.upsert(account.accountId, account)));
    }

    upsert(data: Acct): Observable<Acct> {
        if (data.accountId) {
            return this.update(data, data.accountId);
        }
        return this.create(data);
    }

    /** Delete an account from the system.
     * @param id - account id
     * @returns - observable for request
     */
    delete(id: ID) {
        return this.http
            .delete<Acct>(`${this.config.get().adminBaseUrl}/v1/accounts/${id}`)
            .pipe(tap(() => this.store.remove(id)));
    }

    /** Disable/Enable/Hide (acts as a soft-delete) an account from the system.
     * @param account
     * @param status - describes the wanted status to be set for the entitiy
     * @returns - observable for request
     */
    setStatus(account: Acct, status: AccountStatus) {
        let data: Acct = { ...account };
        // added for backwards compatibility
        if (status === AccountStatus.ENABLED || status === AccountStatus.DISABLED) {
            data.active = status === AccountStatus.ENABLED;
        }

        return this.http
            .put<Acct>(
                `${this.config.get().adminBaseUrl}/v1/accounts/${data.accountId}`,
                {
                    ...data,
                    status
                }
            )
            .pipe(
                tap((acc) => {
                    this.store.update(acc.accountId, acc);
                })
            );
    }

    /** setActiveAccount
     * @description - set active account(s) in the store
     * @param accountId - account id or array of account ids
     */
    setActiveAccount(accountId: Acct['accountId'] | Array<Acct['accountId']>) {
        this.store.setActive(accountId as Array<ID>);
    }

    addTag(accountId: Acct['accountId'], title: AccountTag['title']) {
        /*return this.http
            .post<Acct>(
                `${this.config.get().adminBaseUrl}/v1/accounts/${accountId}/tags`,
                { key, value }
            )
            .pipe(
                tap((account) => {
                    this.store.upsert(accountId, account);
                })
            );
         */
        /*return this._newTagsService.add({ title, accountId }).pipe(
            switchMap((newTag) => {
                // @ts-ignore
                this.store.update(accountId, (entity) => ({
                    ...entity,
                    tags: {
                        ...(entity.tags || {}),
                        [newTag.id]: new Date()
                    }
                }));
                return of(newTag).pipe(delay(0), take(1));
            })
        );*/
        return of(null);
    }

    removeTag(accountId: Acct['accountId'], tagId: NewTag['id']) {
        /*return this.http
            .post<Acct>(
                `${this.config.get().adminBaseUrl}/v1/accounts/${accountId}/tags`,
                { key, value }
            )
            .pipe(
                tap((account) => {
                    this.≤store.upsert(accountId, account);
                })
            );
         */

        /*return this._newTagsService.remove(tagId, { accountId }).pipe(
            switchMap(() => {
                this.store.update(accountId, (entity) => {
                    const { [tagId]: _, ...tags } = entity.tags || {};
                    return {
                        ...entity,
                        tags
                    };
                });
                return of(null).pipe(take(1));
            })
        );*/
        return of(null);
    }
}
